// File: client/src/platform/VariantHost.js
//
// The generic Variant Host. It knows NOTHING variant-specific — no cards, zombies,
// shrinking boards, cooldowns. It:
//   1. resolves the variant module from the registry by config.variantId,
//   2. calls the variant's useRuntime hook to get its runtime (middleware, overlays,
//      victory override, opaque persistence payload),
//   3. renders <GameScreen> wired with that runtime,
//   4. persists/restores the opaque payload (host-level snapshot), and
//   5. offers the rulebook.
//
// Every variant is driven through the same code path here; adding a variant never
// touches this file.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import GameScreen from '../components/GameScreen';
import RulebookModal from './RulebookModal';
import { BookOpen } from 'lucide-react';
import { getVariant } from './variantRegistry';
import { normalizeRuntime } from './variantContract';
import { saveHostSnapshot, loadHostSnapshot, clearHostSnapshot } from './hostPersistence';

const VariantHost = ({ config, onBack }) => {
  const variant = getVariant(config.variantId);

  // Latest chess state reported by GameScreen (ref so the runtime/persistence can
  // read it without re-render churn).
  const chessStateRef = useRef({ gameState: null, moveHistory: [] });
  const [chessTick, setChessTick] = useState(0);
  const gameOverRef = useRef(false);

  // Resume payload: loaded once on mount (local/AI only). null if none.
  const resumeRef = useRef(undefined); // undefined = not yet computed
  if (resumeRef.current === undefined) {
    const snap = variant ? loadHostSnapshot(variant.id, config) : null;
    resumeRef.current = snap || null;
  }
  const resume = resumeRef.current;

  // Call the variant's runtime hook. Host always renders exactly one variant, so
  // this single hook call is Rules-of-Hooks stable. Classic returns null -> no-op.
  const rawRuntime = variant
    ? variant.useRuntime({
        config,
        resumePayload: resume ? resume.payload : null,
        chess: chessStateRef,
      })
    : null;
  const runtime = normalizeRuntime(rawRuntime);

  const [showRulebook, setShowRulebook] = useState(false);

  // --- chess state reporting ---
  const handleStateChange = useCallback(({ gameState, moveHistory }) => {
    chessStateRef.current = { gameState, moveHistory };
    setChessTick(t => t + 1);
  }, []);

  // --- host-level persistence: snapshot on any change (once a board exists) ---
  useEffect(() => {
    if (!variant || gameOverRef.current) return;
    const { gameState, moveHistory } = chessStateRef.current;
    if (!gameState) return; // nothing to save yet
    saveHostSnapshot({
      variantId: variant.id,
      config: { opponent: config.opponent, difficulty: config.difficulty },
      gameState,
      moveHistory,
      payload: runtime.getPersistencePayload(),
    });
    // chessTick captures chess moves; runtime payload changes are captured because
    // the variant re-renders the host (its state lives in useRuntime).
  }, [variant, config, runtime, chessTick]);

  const handleGameOver = useCallback((status) => {
    gameOverRef.current = true;
    clearHostSnapshot();
    runtime.onGameOver(status);
  }, [runtime]);

  const handleBack = useCallback(() => {
    clearHostSnapshot();
    runtime.dispose();
    if (onBack) onBack();
  }, [runtime, onBack]);

  if (!variant) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-4 text-center">
        <p className="text-white text-lg font-semibold">Unknown game mode</p>
        <button onClick={onBack} className="px-4 py-2 bg-slate-600 text-white rounded-lg">
          Back to Menu
        </button>
      </div>
    );
  }

  // Base config handed to GameScreen: the underlying chess mode is ai/local.
  const baseConfig = {
    ...config,
    mode: config.opponent === 'ai' ? 'ai' : 'local',
  };

  const initialGameState =
    (resume && resume.gameState) || runtime.getInitialGameState() || null;
  const initialMoveHistory = (resume && resume.moveHistory) || null;

  // A small rulebook button, injected into the top overlay alongside any variant
  // overlay. Available before the first move and throughout the game.
  const topOverlay = (
    <div className="w-full flex flex-col items-center gap-2">
      <button
        onClick={() => setShowRulebook(true)}
        className="text-xs px-3 py-1 rounded-full bg-white/10 text-slate-200 hover:bg-white/20 transition flex items-center gap-1"
      >
        <BookOpen size={14} /> {variant.name} — How to Play
      </button>
      {runtime.renderOverlay('top')}
    </div>
  );

  return (
    <>
      <GameScreen
        config={baseConfig}
        onBack={handleBack}
        moveMiddleware={runtime.moveMiddleware}
        victoryOverride={runtime.getVictory}
        overlayTop={topOverlay}
        overlayBottom={runtime.renderOverlay('bottom')}
        initialGameState={initialGameState}
        initialMoveHistory={initialMoveHistory}
        onStateChange={handleStateChange}
        onGameOver={handleGameOver}
        flipped={runtime.boardFlipped}
        aiColor={runtime.aiColor}
        pendingMoves={runtime.pendingMoves}
        onMovesApplied={runtime.onMovesApplied || undefined}
      />
      {showRulebook && (
        <RulebookModal
          variantName={variant.name}
          rulebook={variant.rulebook}
          onClose={() => setShowRulebook(false)}
        />
      )}
    </>
  );
};

export default VariantHost;
